import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, useAuth } from '../App';
import Header from '../components/Layout/Header';
import Modal from '../components/UI/Modal';
import StatusPill from '../components/UI/StatusPill';
import { Plus, Eye, ArrowRight } from 'lucide-react';

export default function Transfers() {
  const { user } = useAuth();
  const [transfers, setTransfers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [products, setProducts] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ from_location_id: '', to_location_id: '', product_id: '', quantity: '', notes: '' });

  const [statusFilter, setStatusFilter] = useState('');
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const s = searchParams.get('status');
    if (s) setStatusFilter(s);
  }, [searchParams]);

  const filteredTransfers = useMemo(() => {
    if (!statusFilter) return transfers;
    return transfers.filter(t => t.status === statusFilter);
  }, [transfers, statusFilter]);

  const load = () => api('/api/transfers').then(r => setTransfers(r.data)).catch(console.error);

  useEffect(() => {
    load();
    api('/api/locations').then(r => setLocations(r.data)).catch(() => {});
    api('/api/products').then(r => setProducts(r.data)).catch(() => {});
  }, []);

  const submit = async () => {
    try {
      await api('/api/transfers', { method: 'POST', body: JSON.stringify({ ...form, from_location_id: parseInt(form.from_location_id), to_location_id: parseInt(form.to_location_id), product_id: parseInt(form.product_id), quantity: parseFloat(form.quantity) }) });
      setShowCreate(false); load();
    } catch (err) { alert(err.error || 'Failed'); }
  };

  const approve = async (id) => { await api('/api/transfers/' + id + '/approve', { method: 'PUT' }); load(); setDetail(null); };
  const complete = async (id) => { await api('/api/transfers/' + id + '/complete', { method: 'PUT' }); load(); setDetail(null); };
  const cancel = async (id) => { await api('/api/transfers/' + id + '/cancel', { method: 'PUT' }); load(); setDetail(null); };

  const canManage = ['admin', 'manager'].includes(user?.role);

  const statusColor = { pending: '#F39C12', approved: '#5BC4C0', in_transit: '#78A8E8', completed: '#2ECC71', cancelled: '#E74C3C' };

  return (
    <div>
      <Header title="Transfers">
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}><Plus size={16} /> New Transfer</button>
      </Header>

      <div className="tabs" style={{ marginBottom: 16 }}>
        {['', 'pending', 'approved', 'in_transit', 'completed', 'cancelled'].map(s => (
          <button key={s} className={'tab' + (statusFilter === s ? ' active' : '')} onClick={() => setStatusFilter(s)}>
            {s === 'in_transit' ? 'In Transit' : s || 'All'}
          </button>
        ))}
      </div>

      <div className="table-wrap">
        <table>
          <thead><tr><th>Product</th><th>From</th><th></th><th>To</th><th>Qty</th><th>Status</th><th>Requested By</th><th>Date</th><th>Actions</th></tr></thead>
          <tbody>
            {filteredTransfers.map(t => (
              <tr key={t.id}>
                <td style={{ fontWeight: 600 }}>{t.product_name}</td>
                <td>{t.from_location_name}</td>
                <td style={{ textAlign: 'center', color: 'var(--faint)' }}><ArrowRight size={14} /></td>
                <td>{t.to_location_name}</td>
                <td>{Number(t.quantity).toFixed(0)} {t.unit_type}</td>
                <td><StatusPill status={t.status} /></td>
                <td>{t.requested_by_name}</td>
                <td style={{ color: 'var(--faint)', fontSize: 13 }}>{new Date(t.requested_at).toLocaleDateString()}</td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => setDetail(t)} title="View"><Eye size={14} /></button>
                    {canManage && t.status === 'pending' && <button className="btn btn-primary btn-sm" onClick={() => approve(t.id)}>Approve</button>}
                    {canManage && (t.status === 'approved' || t.status === 'in_transit') && <button className="btn btn-primary btn-sm" onClick={() => complete(t.id)}>Complete</button>}
                    {canManage && ['pending', 'approved'].includes(t.status) && <button className="btn btn-danger btn-sm" onClick={() => cancel(t.id)}>Cancel</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* View Detail Modal */}
      {detail && (
        <Modal title="Transfer Detail" onClose={() => setDetail(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 16, background: 'var(--card)', borderRadius: 10 }}>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>From</div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{detail.from_location_name}</div>
              </div>
              <ArrowRight size={20} style={{ color: 'var(--gold)', flexShrink: 0 }} />
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>To</div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{detail.to_location_name}</div>
              </div>
            </div>

            <div className="grid-2">
              <div><span style={{ fontSize: 12, color: 'var(--muted)' }}>Product</span><div style={{ fontWeight: 600, marginTop: 2 }}>{detail.product_name}</div></div>
              <div><span style={{ fontSize: 12, color: 'var(--muted)' }}>SKU</span><div style={{ color: 'var(--faint)', marginTop: 2 }}>{detail.sku || '-'}</div></div>
              <div><span style={{ fontSize: 12, color: 'var(--muted)' }}>Quantity</span><div style={{ fontWeight: 600, marginTop: 2, fontSize: 18, color: 'var(--gold)' }}>{Number(detail.quantity).toFixed(0)} {detail.unit_type}</div></div>
              <div><span style={{ fontSize: 12, color: 'var(--muted)' }}>Status</span><div style={{ marginTop: 4 }}><StatusPill status={detail.status} /></div></div>
              <div><span style={{ fontSize: 12, color: 'var(--muted)' }}>Requested By</span><div style={{ marginTop: 2 }}>{detail.requested_by_name}</div></div>
              <div><span style={{ fontSize: 12, color: 'var(--muted)' }}>Date</span><div style={{ marginTop: 2 }}>{new Date(detail.requested_at).toLocaleDateString()}</div></div>
              {detail.approved_by_name && <div><span style={{ fontSize: 12, color: 'var(--muted)' }}>Approved By</span><div style={{ marginTop: 2 }}>{detail.approved_by_name}</div></div>}
              {detail.completed_at && <div><span style={{ fontSize: 12, color: 'var(--muted)' }}>Completed</span><div style={{ marginTop: 2 }}>{new Date(detail.completed_at).toLocaleDateString()}</div></div>}
            </div>

            {detail.notes && (
              <div><span style={{ fontSize: 12, color: 'var(--muted)' }}>Notes</span><div style={{ marginTop: 4, padding: 12, background: 'var(--navy)', borderRadius: 8, fontSize: 13 }}>{detail.notes}</div></div>
            )}
          </div>

          {canManage && (
            <div className="modal-actions">
              {detail.status === 'pending' && <button className="btn btn-primary" onClick={() => approve(detail.id)}>Approve Transfer</button>}
              {(detail.status === 'approved' || detail.status === 'in_transit') && <button className="btn btn-primary" onClick={() => complete(detail.id)}>Mark Completed</button>}
              {['pending', 'approved'].includes(detail.status) && <button className="btn btn-danger" onClick={() => cancel(detail.id)}>Cancel Transfer</button>}
            </div>
          )}
        </Modal>
      )}

      {showCreate && (
        <Modal title="New Transfer" onClose={() => setShowCreate(false)}>
          <div className="form-group"><label>Product</label>
            <select value={form.product_id} onChange={e => setForm({...form, product_id: e.target.value})} required>
              <option value="">Select...</option>
              {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="grid-2">
            <div className="form-group"><label>From Location</label>
              <select value={form.from_location_id} onChange={e => setForm({...form, from_location_id: e.target.value})} required>
                <option value="">Select...</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>To Location</label>
              <select value={form.to_location_id} onChange={e => setForm({...form, to_location_id: e.target.value})} required>
                <option value="">Select...</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group"><label>Quantity</label><input type="number" value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})} min="0.1" step="0.1" required /></div>
          <div className="form-group"><label>Notes</label><textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} /></div>
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={submit}>Request Transfer</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
